import React, { createContext, useContext, useState, useCallback } from 'react';
import { coerceDurationToSeconds } from '../utils/time';

// Context to share app-level state between components
// Replaces prop drilling for shared state like meeting details, loading, etc.
const AppContext = createContext(null);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Meeting details state - used when viewing/resuming a meeting from history
  const [details, setDetails] = useState({});
  const [meetingAPIDetails, setMeetingAPIDetails] = useState({});

  // Loading state
  const [loading, setLoading] = useState(false);

  // Terms and conditions state
  const [termsConditionFlag, setTermsConditionFlag] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(true);

  // Format utilities
  const formatDate = useCallback((dateString) => {
    const inputDate = new Date(dateString);
    const today = new Date();

    const input = new Date(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate()
    );
    const current = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const oneDay = 24 * 60 * 60 * 1000;
    const diffDays = (input - current) / oneDay;

    let label;
    if (diffDays === 0) {
      label = 'Today';
    } else if (diffDays === -1) {
      label = 'Yesterday';
    } else {
      label = inputDate.toLocaleDateString('en-US', { weekday: 'long' });
    }

    const day = inputDate.getDate().toString().padStart(2, '0');
    const month = (inputDate.getMonth() + 1).toString().padStart(2, '0');
    const year = inputDate.getFullYear().toString().slice(-2);

    return `${label}, ${day}/${month}/${year}`;
  }, []);

  const formatDuration = useCallback((seconds) => {
    const hrs = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, '0');
    const mins = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');

    return `${hrs} : ${mins} : ${secs}`;
  }, []);

  const formatTime = useCallback((totalSeconds) => {
    const safeSeconds = coerceDurationToSeconds(totalSeconds);
    const seconds = Math.floor(safeSeconds);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    const paddedHours = hours.toString().padStart(2, '0');
    const paddedMinutes = minutes.toString().padStart(2, '0');
    const paddedSecs = secs.toString().padStart(2, '0');
    return `${paddedHours}:${paddedMinutes}:${paddedSecs}`;
  }, []);

  const formatDateTime = useCallback((datetimeString) => {
    const inputDate = new Date(datetimeString.replace(' ', 'T'));
    const today = new Date();

    const input = new Date(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate()
    );
    const current = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const oneDay = 24 * 60 * 60 * 1000;
    const diffDays = (input - current) / oneDay;

    let label;
    if (diffDays === 0) label = 'Today';
    else if (diffDays === -1) label = 'Yesterday';
    else label = inputDate.toLocaleDateString('en-US', { weekday: 'long' });

    const day = inputDate.getDate().toString().padStart(2, '0');
    const month = (inputDate.getMonth() + 1).toString().padStart(2, '0');
    const year = inputDate.getFullYear().toString().slice(-2);

    const dateFormatted = `${label}, ${day}/${month}/${year}`;

    const timeFormatted = inputDate
      .toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      })
      .toLowerCase()
      .replace('am', 'a.m.')
      .replace('pm', 'p.m.');

    return [dateFormatted, timeFormatted];
  }, []);

  const value = {
    // Meeting details
    details,
    setDetails,
    meetingAPIDetails,
    setMeetingAPIDetails,

    // Loading
    loading,
    setLoading,

    // Terms
    termsConditionFlag,
    setTermsConditionFlag,
    termsAccepted,
    setTermsAccepted,

    // Format utilities
    formatDate,
    formatDuration,
    formatTime,
    formatDateTime,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
